// Section for import npm components -----------------------------------------------------------------------------------
import React from 'react';
import { createRoot } from 'react-dom/client';
import parse from 'html-react-parser';

// Material UI ( mui )  components
import Box from '@mui/material/Box';

// Section for import custom components --------------------------------------------------------------------------------
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';

import NotificationForSale from '@components/MansionDetail/NotificationForSale';
import MansionImage from '@components/MansionDetail/MansionImage';
import PickupReview from '@components/MansionDetail/PickupReview';
import MansionContractHistories from '@components/MansionDetail/MansionContractHistories';
import MansionRenovationHistoriesPickUp from '@components/MansionDetail/MansionRenovationHistoriesPickUp';
import MansionInquiryInContents from '@components/MansionDetail/MansionInquiryInContents';
import MansionLabel from '@components/MansionDetail/MansionLabel';
import MansionReview from '@components/MansionDetail/MansionReview';
import ThreeStep from '@components/MansionDetail/ThreeStep';
import MansionSalesHistory from '@components/MansionDetail/MansionSalesHistory';
import MansionMarketPriceHistory from '@components/MansionDetail/MansionMarketPriceHistory';
import RentHistory from '@components/MansionDetail/RentHistory';
import RentMarketPriceHistory from '@components/MansionDetail/RentMarketPriceHistory';
import MansionInquiry from '@components/MansionDetail/MansionInquiry';
import MansionOverView from '@components/MansionDetail/MansionOverView';
import ConsultationMansion from '@components/MansionDetail/ConsultationMansion';
import MansionOverviewDescription from '@components/MansionDetail/MansionOverviewDescription';
import MansionAroundData from '@components/MansionDetail/MansionAroundData';
import MansionNearOnSale from '@components/MansionDetail/MansionNearOnSale';
import MansionNear from '@components/MansionDetail/MansionNear';
import MansionRenovationHistories from '@components/MansionDetail/MansionRenovationHistories';
import MansionShop from '@components/MansionDetail/MansionShop';
import Breadcrumbs from '@components/common/Breadcrumbs';
import App from '../app';

// Section for type definition -----------------------------------------------------------------------------------------
import MansionDetailPropsData from '@/types/components/MansionDetail/MansionDetailPropsData';

const targetId: string = 'app-content-detail';
const targetIdTitle: string = 'app-content-detail-title';
const targetIdRoomsForSale: string = 'app-content-detail-roomsForSale';
const targetIdMansionInformation: string = 'app-content-detail-mansionInformation';
const targetIdYourStyle: string = 'app-content-detail-your-style';
const targetIdFaq: string = 'app-content-detail-faq';

function MansionDetail() {
  const container = document.getElementById('app-content-detail-context')!;
  const reactElement = parse(container.innerHTML);
  return <Box>{reactElement}</Box>;
}

// Section for render component ----------------------------------------------------------------------------------------

// Render components if the targetId element exists in the DOM.
if (document.getElementById(targetId)) {
  const container = document.getElementById(targetId)!;
  const root = createRoot(container);

  const { props } = container.dataset;
  const propsData: MansionDetailPropsData = JSON.parse(props!);

  const {
    mansionName,
    images,
    isMansionImages,
    estateHelpContactUrl,
    mansionId,
    address,
    mansionTransit,
    buildCreatedDate,
    petStatus,
    nearEstates,
    nearMansions,
    pickupReview,
  } = propsData;

  const breadcrumbs = container.dataset.breadcrumbs ? JSON.parse(container.dataset.breadcrumbs) : [];

  const mansionReview = container.dataset.mansionReview ? JSON.parse(container.dataset.mansionReview) : false;
  const mansionEstates = propsData.mansionEstates || [];
  const salesHistory = container.dataset.salesHistory ? JSON.parse(container.dataset.salesHistory) : false;
  const marketPriceHistory = container.dataset.marketPriceHistory
    ? JSON.parse(container.dataset.marketPriceHistory)
    : false;

  const rentHistory = container.dataset.rentHistory ? JSON.parse(container.dataset.rentHistory) : false;

  const rentMarketPriceHistory = container.dataset.rentMarketPriceHistory
    ? JSON.parse(container.dataset.rentMarketPriceHistory)
    : false;

  const mansionAroundExtend = container.dataset.mansionAroundExtend
    ? JSON.parse(container.dataset.mansionAroundExtend)
    : false;

  const mansionRenovationHistory = container.dataset.mansionRenovationHistory
    ? JSON.parse(container.dataset.mansionRenovationHistory)
    : false;

  const mansionOverviewDescription = container.dataset.mansionOverviewDescription
    ? JSON.parse(container.dataset.mansionOverviewDescription)
    : false;

  const shop = container.dataset.shop ? JSON.parse(container.dataset.shop) : false;

  const mansionContractHistory = container.dataset.mansionContractHistory
    ? JSON.parse(container.dataset.mansionContractHistory)
    : false;

  const targetTitle = document.getElementById(targetIdTitle)!;
  const title = parse(targetTitle.innerHTML);

  const targetRoomsForSale = document.getElementById(targetIdRoomsForSale)!;
  const roomsForSale = parse(targetRoomsForSale.innerHTML);

  const targetMansionInformation = document.getElementById(targetIdMansionInformation)!;
  const mansionInformation = parse(targetMansionInformation.innerHTML);

  const targetYourStyle = document.getElementById(targetIdYourStyle)!;
  const yourStyle = parse(targetYourStyle.innerHTML);

  const targetFaq = document.getElementById(targetIdFaq)!;
  const faq = parse(targetFaq.innerHTML);

  root.render(
    <App>
      <Header />
      {mansionEstates.length > 0 && <NotificationForSale mansionEstates={mansionEstates} />}
      {title}
      <MansionImage mansionName={mansionName} images={images} isMansionImages={isMansionImages} />
      <MansionLabel
        mansionContractHistory={mansionContractHistory}
        mansionRenovationHistory={mansionRenovationHistory}
      />
      <Box className="py-3 max-md:py-0" />
      <MansionOverView
        address={address}
        mansionTransit={mansionTransit}
        buildCreatedDate={buildCreatedDate}
        petStatus={petStatus}
      />
      {(pickupReview && Array.isArray(pickupReview)) || mansionContractHistory || mansionRenovationHistory ? (
        <Box className="pt-6 max-md:pt-2" />
      ) : null}
      {pickupReview && Array.isArray(pickupReview) && <PickupReview pickupReview={pickupReview} />}
      {mansionContractHistory && <MansionContractHistories mansionContractHistory={mansionContractHistory} />}
      {mansionRenovationHistory && (
        <MansionRenovationHistoriesPickUp mansionRenovationHistories={mansionRenovationHistory} />
      )}
      {(pickupReview && Array.isArray(pickupReview)) || mansionContractHistory || mansionRenovationHistory ? (
        <Box className="pb-6 max-md:pb-2" />
      ) : null}
      {roomsForSale}
      <MansionInquiryInContents mansionName={mansionName} estateHelpContactUrl={estateHelpContactUrl} />
      {mansionInformation}
      {mansionOverviewDescription && (
        <MansionOverviewDescription mansionOverviewDescription={mansionOverviewDescription} />
      )}
      {mansionAroundExtend && <MansionAroundData mansionAroundExtend={mansionAroundExtend} />}
      <MansionSalesHistory salesHistory={salesHistory} />
      <MansionMarketPriceHistory marketPriceHistory={marketPriceHistory} mansionId={mansionId} />
      <RentHistory rentHistory={rentHistory} />
      <RentMarketPriceHistory rentMarketPriceHistory={rentMarketPriceHistory} mansionId={mansionId} />
      <ConsultationMansion />
      <MansionReview mansionReview={mansionReview} />
      <ThreeStep />
      <MansionNearOnSale mansionName={mansionName} mansionData={nearEstates} />
      {mansionRenovationHistory && (
        <MansionRenovationHistories mansionName={mansionName} mansionRenovationHistories={mansionRenovationHistory} />
      )}
      {yourStyle}
      {faq}
      <MansionNear mansionName={mansionName} mansionData={nearMansions} />
      {shop && <MansionShop shop={shop} />}
      <MansionDetail />
      <MansionInquiry estateHelpContactUrl={estateHelpContactUrl} />
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Footer />
    </App>,
  );
}
